<template>
  <div class="be">
    <div class="be-topbar">
      <div class="be-topbar-left">
        <button class="be-back" @click="$router.push({ name: 'app-blog' })">
          <feather-icon icon="ArrowLeftIcon" size="16" />
        </button>
        <div class="be-topbar-title">
          <h5>{{ isEditing ? 'Editar Post' : 'Novo Post' }}</h5>
          <span v-if="form.slug" class="be-slug">/blog/{{ form.slug }}</span>
        </div>
      </div>
      <div class="be-topbar-actions">
        <button class="be-btn be-btn-ghost" @click="savePost('rascunho')">
          <feather-icon icon="SaveIcon" size="14" />
          Rascunho
        </button>
        <button class="be-btn be-btn-outline" @click="savePost('pendente_aprovacao')" v-if="!isEditing || form.status === 'rascunho'">
          <feather-icon icon="SendIcon" size="14" />
          Aprovação
        </button>
        <button class="be-btn be-btn-primary" @click="savePost('publicado')">
          <feather-icon icon="GlobeIcon" size="14" />
          Publicar
        </button>
      </div>
    </div>

    <div v-if="statusMessage" class="be-toast" :class="'be-toast-' + statusVariant" @click="statusMessage = ''">
      <feather-icon :icon="statusVariant === 'success' ? 'CheckCircleIcon' : statusVariant === 'warning' ? 'AlertTriangleIcon' : 'XCircleIcon'" size="16" />
      <span>{{ statusMessage }}</span>
      <feather-icon icon="XIcon" size="14" class="be-toast-close" />
    </div>

    <div class="be-layout">
      <div class="be-main">
        <div class="be-tabs">
          <button v-for="(tab, i) in tabs" :key="i" class="be-tab" :class="{ active: activeTab === i }" @click="activeTab = i">
            <feather-icon :icon="tab.icon" size="14" />
            {{ tab.label }}
          </button>
        </div>

        <div class="be-panel" v-show="activeTab === 0">
          <div class="be-field">
            <label class="be-label">Título</label>
            <input v-model="form.titulo" placeholder="Um título chamativo para seu artigo..." class="be-input be-input-lg" @input="autoSlug" />
          </div>
          <div class="be-field">
            <label class="be-label">Slug (URL)</label>
            <div class="be-slug-field">
              <span class="be-slug-prefix">/blog/</span>
              <input v-model="form.slug" placeholder="slug-do-post" class="be-input be-input-slug" />
            </div>
          </div>
          <div class="be-field">
            <label class="be-label">Resumo</label>
            <textarea v-model="form.resumo" rows="2" placeholder="Uma frase que resume seu artigo e atrai o leitor..." class="be-input be-textarea"></textarea>
            <span class="be-counter" :class="{ over: (form.resumo || '').length > 155 }">{{ (form.resumo || '').length }}/155</span>
          </div>
          <div class="be-field">
            <label class="be-label">Conteúdo</label>
            <rich-text-editor v-model="form.conteudo" />
          </div>
        </div>

        <div class="be-panel" v-show="activeTab === 1">
          <div class="be-seo-grid">
            <div class="be-seo-card">
              <div class="be-seo-card-icon"><feather-icon icon="TypeIcon" size="16" /></div>
              <div class="be-seo-card-body">
                <label class="be-label">Meta Title</label>
                <input v-model="form.meta_title" placeholder="Título otimizado para buscadores (30-60 chars)" class="be-input" maxlength="70" />
                <div class="be-char-bar">
                  <div class="be-char-fill" :style="{ width: Math.min(metaTitleLen / 60 * 100, 100) + '%' }" :class="metaTitleLen > 60 ? 'over' : metaTitleLen >= 30 ? 'ok' : ''"></div>
                </div>
                <span class="be-counter" :class="{ over: metaTitleLen > 60 }">{{ metaTitleLen }}/60</span>
              </div>
            </div>
            <div class="be-seo-card">
              <div class="be-seo-card-icon"><feather-icon icon="AlignLeftIcon" size="16" /></div>
              <div class="be-seo-card-body">
                <label class="be-label">Meta Description</label>
                <textarea v-model="form.meta_description" rows="2" placeholder="Descrição persuasiva com call-to-action (120-155 chars)" class="be-input be-textarea" maxlength="160"></textarea>
                <div class="be-char-bar">
                  <div class="be-char-fill" :style="{ width: Math.min(metaDescLen / 155 * 100, 100) + '%' }" :class="metaDescLen > 155 ? 'over' : metaDescLen >= 120 ? 'ok' : ''"></div>
                </div>
                <span class="be-counter" :class="{ over: metaDescLen > 155 }">{{ metaDescLen }}/155</span>
              </div>
            </div>
            <div class="be-seo-card">
              <div class="be-seo-card-icon"><feather-icon icon="TagIcon" size="16" /></div>
              <div class="be-seo-card-body">
                <label class="be-label">Keywords</label>
                <input v-model="form.keywords" placeholder="vendas online, marketplace, e-commerce, dicas" class="be-input" />
                <span class="be-counter">{{ keywordsCount }} palavra(s)-chave</span>
              </div>
            </div>
            <div class="be-seo-card">
              <div class="be-seo-card-icon"><feather-icon icon="ImageIcon" size="16" /></div>
              <div class="be-seo-card-body">
                <label class="be-label">OG Image URL</label>
                <input v-model="form.og_image" placeholder="URL da imagem para redes sociais (1200x630px)" class="be-input" />
              </div>
            </div>
            <div class="be-seo-card">
              <div class="be-seo-card-icon"><feather-icon icon="LinkIcon" size="16" /></div>
              <div class="be-seo-card-body">
                <label class="be-label">Canonical URL</label>
                <input v-model="form.canonical_url" placeholder="https://seusite.com/blog/artigo" class="be-input" />
              </div>
            </div>
          </div>
          <button class="be-btn be-btn-primary mt-2" :disabled="aiLoading" @click="generateSeo">
            <b-spinner v-if="aiLoading && aiAction === 'seo'" small />
            <feather-icon v-else icon="CpuIcon" size="14" />
            Gerar SEO com IA
          </button>
        </div>

        <div class="be-panel" v-show="activeTab === 2">
          <div class="be-ai-grid">
            <div class="be-ai-card">
              <div class="be-ai-card-top">
                <div class="be-ai-icon purple"><feather-icon icon="ZapIcon" size="20" /></div>
                <div>
                  <h6>Gerar Artigo Completo</h6>
                  <p>Cria um artigo otimizado com título, conteúdo, resumo e SEO</p>
                </div>
              </div>
              <textarea v-model="aiIdea" rows="3" placeholder="Sobre o que deseja escrever? Ex: Tendências de e-commerce em 2025..." class="be-input be-textarea"></textarea>
              <button class="be-btn be-btn-primary be-btn-block" :disabled="!aiIdea || aiLoading" @click="generateArticle">
                <b-spinner v-if="aiLoading && aiAction === 'generate'" small />
                <feather-icon v-else icon="FileTextIcon" size="14" />
                Gerar Artigo
              </button>
            </div>

            <div class="be-ai-card">
              <div class="be-ai-card-top">
                <div class="be-ai-icon blue"><feather-icon icon="Edit3Icon" size="20" /></div>
                <div>
                  <h6>Melhorar Conteúdo</h6>
                  <p>Reescreve tornando mais envolvente, bem estruturado e profissional</p>
                </div>
              </div>
              <button class="be-btn be-btn-outline be-btn-block" :disabled="!form.conteudo || aiLoading" @click="improveContent">
                <b-spinner v-if="aiLoading && aiAction === 'improve'" small />
                <feather-icon v-else icon="TrendingUpIcon" size="14" />
                Melhorar Conteúdo
              </button>
            </div>

            <div class="be-ai-card">
              <div class="be-ai-card-top">
                <div class="be-ai-icon amber"><feather-icon icon="AwardIcon" size="20" /></div>
                <div>
                  <h6>Avaliar Qualidade</h6>
                  <p>Score de 1-10, pontos fortes e sugestões de melhoria detalhadas</p>
                </div>
              </div>
              <button class="be-btn be-btn-outline be-btn-block" :disabled="!form.conteudo || aiLoading" @click="reviewContent">
                <b-spinner v-if="aiLoading && aiAction === 'review'" small />
                <feather-icon v-else icon="StarIcon" size="14" />
                Avaliar Artigo
              </button>
              <div v-if="reviewResult" class="be-review">
                <div class="be-review-score">
                  <span class="be-review-num" :class="reviewResult.score >= 7 ? 'good' : reviewResult.score >= 5 ? 'mid' : 'low'">{{ reviewResult.score }}</span>
                  <span class="be-review-of">/10</span>
                </div>
                <div v-if="reviewResult.strengths && reviewResult.strengths.length" class="be-review-list good">
                  <strong><feather-icon icon="CheckCircleIcon" size="13" /> Pontos Fortes</strong>
                  <ul><li v-for="(s, i) in reviewResult.strengths" :key="'s'+i">{{ s }}</li></ul>
                </div>
                <div v-if="reviewResult.improvements && reviewResult.improvements.length" class="be-review-list warn">
                  <strong><feather-icon icon="AlertCircleIcon" size="13" /> Melhorias</strong>
                  <ul><li v-for="(m, i) in reviewResult.improvements" :key="'m'+i">{{ m }}</li></ul>
                </div>
                <p v-if="reviewResult.summary" class="be-review-summary">{{ reviewResult.summary }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="be-panel" v-show="activeTab === 3">
          <word-press-import @imported="onWordPressImported" />
        </div>
      </div>

      <div class="be-sidebar">
        <div class="be-side-card">
          <div class="be-side-label"><feather-icon icon="FolderIcon" size="14" /> Categoria</div>
          <select v-model="form.id_categoria" class="be-input be-select">
            <option :value="null">Sem categoria</option>
            <option v-for="cat in categorias" :key="cat.id" :value="cat.id">{{ cat.nome }}</option>
          </select>
          <button class="be-link" @click="showNewCat = !showNewCat">
            <feather-icon icon="PlusIcon" size="12" /> Nova categoria
          </button>
          <div v-if="showNewCat" class="be-inline-form">
            <input v-model="newCatName" placeholder="Nome" class="be-input" />
            <input v-model="newCatColor" type="color" class="be-color" />
            <button class="be-btn be-btn-primary be-btn-sm" @click="createCategory" :disabled="!newCatName">Criar</button>
          </div>
        </div>

        <div class="be-side-card">
          <div class="be-side-label"><feather-icon icon="ImageIcon" size="14" /> Imagem de Destaque</div>
          <div v-if="form.imagem_destaque" class="be-img-preview">
            <img :src="form.imagem_destaque" alt="Preview" />
            <button class="be-img-remove" @click="form.imagem_destaque = ''"><feather-icon icon="XIcon" size="14" /></button>
          </div>
          <div v-else class="be-img-dropzone">
            <feather-icon icon="UploadCloudIcon" size="24" />
            <span>Enviar imagem</span>
            <b-form-file accept="image/*" plain class="be-img-input" @input="uploadImagem" :disabled="uploading" />
          </div>
          <input v-model="form.imagem_destaque" placeholder="ou cole a URL da imagem" class="be-input mt-1" />
          <button class="be-link" :disabled="!form.titulo || aiLoading" @click="generateFeaturedImage">
            <b-spinner v-if="aiLoading && aiAction === 'generate-image'" small />
            <feather-icon v-else icon="CpuIcon" size="12" />
            Gerar imagem com IA
          </button>
        </div>

        <seo-score-card
          :meta-title="form.meta_title"
          :meta-description="form.meta_description"
          :keywords="form.keywords"
          :excerpt="form.resumo"
          :featured-image="form.imagem_destaque"
          :content="form.conteudo"
          :optimizing="aiAction === 'seo-optimize'"
          @optimize="seoOptimize"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BButton, BButtonGroup, BRow, BCol,
  BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormFile,
  BAlert, BSpinner, BModal, BTabs, BTab, BInputGroup, BInputGroupAppend,
  BBadge,
} from 'bootstrap-vue'
import axios from '@axios'
import RichTextEditor from './RichTextEditor.vue'
import SEOScoreCard from './SEOScoreCard.vue'
import WordPressImport from './WordPressImport.vue'

export default {
  components: {
    BCard, BCardHeader, BCardBody, BButton, BButtonGroup, BRow, BCol,
    BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormFile,
    BAlert, BSpinner, BModal, BTabs, BTab, BInputGroup, BInputGroupAppend,
    BBadge,
    'rich-text-editor': RichTextEditor,
    'seo-score-card': SEOScoreCard,
    'word-press-import': WordPressImport,
  },
  data() {
    const maxSizeEnv = Number(process.env.VUE_APP_IMAGE_MAX_SIZE_MB)
    const maxImageSizeMb = Number.isFinite(maxSizeEnv) && maxSizeEnv > 0 ? maxSizeEnv : 5

    return {
      form: {
        titulo: '',
        slug: '',
        conteudo: '',
        resumo: '',
        imagem_destaque: '',
        id_categoria: null,
        meta_title: '',
        meta_description: '',
        keywords: '',
        og_image: '',
        canonical_url: '',
        status: 'rascunho',
      },
      categorias: [],
      isEditing: false,
      postId: null,
      uploading: false,
      maxImageSizeMb,
      maxImageSize: maxImageSizeMb * 1024 * 1024,
      maxImageDimension: 1920,
      compressionQuality: 0.92,
      allowedImageTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'],
      aiLoading: false,
      aiAction: '',
      aiIdea: '',
      reviewResult: null,
      showNewCat: false,
      newCatName: '',
      newCatColor: '#F26522',
      statusMessage: '',
      statusVariant: 'success',
      activeTab: 0,
      tabs: [
        { label: 'Editor', icon: 'EditIcon' },
        { label: 'SEO', icon: 'SearchIcon' },
        { label: 'IA', icon: 'CpuIcon' },
        { label: 'Importar', icon: 'DownloadIcon' },
      ],
    }
  },
  computed: {
    metaTitleLen() { return (this.form.meta_title || '').length },
    metaDescLen() { return (this.form.meta_description || '').length },
    keywordsCount() { return (this.form.keywords || '').split(',').filter(k => k.trim()).length },
  },
  created() {
    if (!this.$store.hasModule('blog')) {
      this.$store.registerModule('blog', require('./blogStoreModule').default)
    }
    this.fetchCategorias()
    if (this.$route.params.id) {
      this.isEditing = true
      this.postId = this.$route.params.id
      this.loadPost()
    }
  },
  methods: {
    resolveApiUrl(value) {
      if (!value) return ''

      const trimmed = String(value).trim()
      if (!trimmed) return ''

      if (/^(https?:)?\/\//i.test(trimmed) || /^data:|^blob:/i.test(trimmed)) {
        return trimmed
      }

      const rawBaseUrl = (axios?.defaults?.baseURL || '').replace(/\/+$/, '')
      const baseUrl = rawBaseUrl.replace(/\/api\/v1$/i, '').replace(/\/api$/i, '')
      if (!baseUrl) return trimmed

      if (trimmed.startsWith('/')) {
        return `${baseUrl}${trimmed}`
      }

      return `${baseUrl}/${trimmed}`
    },
    showImageWarning(message) {
      this.statusMessage = message
      this.statusVariant = 'warning'
    },
    validateImageType(file) {
      return this.allowedImageTypes.includes(file.type)
    },
    loadImage(file) {
      return new Promise((resolve, reject) => {
        const image = new Image()
        const objectUrl = URL.createObjectURL(file)

        image.onload = () => {
          URL.revokeObjectURL(objectUrl)
          resolve(image)
        }

        image.onerror = () => {
          URL.revokeObjectURL(objectUrl)
          reject(new Error('Não foi possível ler a imagem selecionada.'))
        }

        image.src = objectUrl
      })
    },
    canvasToFile(canvas, file, mimeType, quality) {
      return new Promise(resolve => {
        canvas.toBlob(blob => {
          if (!blob) {
            resolve(file)
            return
          }

          resolve(new File([blob], file.name, {
            type: mimeType,
            lastModified: file.lastModified,
          }))
        }, mimeType, quality)
      })
    },
    hasTransparency(context, width, height) {
      const { data } = context.getImageData(0, 0, width, height)

      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
          return true
        }
      }

      return false
    },
    async compressImage(file) {
      if (file.type === 'image/gif') {
        return file
      }

      const image = await this.loadImage(file)
      const needsResize = image.width > this.maxImageDimension || image.height > this.maxImageDimension
      const needsCompress = file.size > this.maxImageSize

      if (!needsResize && !needsCompress) {
        return file
      }

      const scale = Math.min(
        this.maxImageDimension / image.width,
        this.maxImageDimension / image.height,
        1,
      )
      const width = Math.max(1, Math.round(image.width * scale))
      const height = Math.max(1, Math.round(image.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height

      const context = canvas.getContext('2d')
      context.drawImage(image, 0, 0, width, height)

      const isPng = file.type === 'image/png'
      const mimeType = isPng && this.hasTransparency(context, width, height)
        ? 'image/png'
        : 'image/jpeg'
      const quality = mimeType === 'image/jpeg' ? this.compressionQuality : undefined

      const compressedFile = await this.canvasToFile(canvas, file, mimeType, quality)

      if (compressedFile.size >= file.size) {
        return file
      }

      return compressedFile
    },
    async prepareImage(file) {
      if (!this.validateImageType(file)) {
        this.showImageWarning('Selecione apenas imagens JPEG, PNG ou GIF.')
        return null
      }

      const compressedFile = await this.compressImage(file)

      if (compressedFile.size > this.maxImageSize) {
        this.showImageWarning(`A imagem ainda ficou acima de ${this.maxImageSizeMb}MB após a compressão automática.`)
        return null
      }

      return compressedFile
    },
    autoSlug() {
      if (!this.isEditing) {
        this.form.slug = this.form.titulo
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .substring(0, 270)
      }
    },
    async fetchCategorias() {
      try {
        const { data } = await this.$store.dispatch('blog/fetchCategorias')
        this.categorias = data
      } catch (e) { console.error(e) }
    },
    async loadPost() {
      try {
        const { data } = await this.$store.dispatch('blog/fetchPost', this.postId)
        this.form = {
          titulo: data.titulo || '',
          slug: data.slug || '',
          conteudo: data.conteudo || '',
          resumo: data.resumo || '',
          imagem_destaque: this.resolveApiUrl(data.imagem_destaque || ''),
          id_categoria: data.id_categoria || null,
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || '',
          keywords: data.keywords || '',
          og_image: this.resolveApiUrl(data.og_image || ''),
          canonical_url: data.canonical_url || '',
          status: data.status || 'rascunho',
        }
      } catch (e) {
        this.statusMessage = 'Erro ao carregar post'
        this.statusVariant = 'danger'
      }
    },
    async savePost(status) {
      try {
        const payload = { ...this.form, status }
        if (!payload.slug) {
          payload.slug = this.form.titulo
            .toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') + '-' + Date.now()
        }
        if (this.isEditing) {
          await this.$store.dispatch('blog/updatePost', { id: this.postId, data: payload })
          this.statusMessage = status === 'publicado' ? 'Post publicado!' : status === 'pendente_aprovacao' ? 'Enviado para aprovação!' : 'Rascunho salvo!'
        } else {
          const { data } = await this.$store.dispatch('blog/createPost', payload)
          this.isEditing = true
          this.postId = data.id
          this.statusMessage = status === 'publicado' ? 'Post criado e publicado!' : 'Rascunho criado!'
        }
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = e.response?.data?.error || 'Erro ao salvar'
        this.statusVariant = 'danger'
      }
    },
    async uploadImagem(file) {
      const selectedFile = Array.isArray(file) ? file[0] : file
      if (!selectedFile) return

      const preparedFile = await this.prepareImage(selectedFile)
      if (!preparedFile) return

      this.uploading = true
      try {
        const fd = new FormData()
        fd.append('image', preparedFile)
        const { data } = await axios.post('/upload', fd)
        const imageUrl = data.url || data.path || data.image_url || data.caminho_imagem
        this.form.imagem_destaque = this.resolveApiUrl(imageUrl)
      } catch (e) {
        this.statusMessage = 'Erro ao enviar imagem'
        this.statusVariant = 'danger'
      }
      this.uploading = false
    },
    async generateArticle() {
      if (!this.aiIdea.trim()) return
      this.aiLoading = true
      this.aiAction = 'generate'
      try {
        const { data } = await this.$store.dispatch('blog/blogAi', { action: 'generate', idea: this.aiIdea })
        const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result
        this.form.titulo = result.title || result.titulo || ''
        this.form.conteudo = result.content || result.conteudo || ''
        this.form.resumo = result.excerpt || result.resumo || ''
        if (result.meta_title) this.form.meta_title = result.meta_title
        if (result.meta_description) this.form.meta_description = result.meta_description
        if (result.keywords) this.form.keywords = result.keywords
        this.autoSlug()
        this.statusMessage = 'Artigo gerado pela IA!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro na geração com IA: ' + (e.response?.data?.error || e.message || 'verifique a chave da API')
        this.statusVariant = 'danger'
      }
      this.aiLoading = false
      this.aiAction = ''
    },
    async improveContent() {
      if (!this.form.conteudo) return
      this.aiLoading = true
      this.aiAction = 'improve'
      try {
        const { data } = await this.$store.dispatch('blog/blogAi', {
          action: 'improve', title: this.form.titulo, content: this.form.conteudo,
        })
        const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result
        if (result.title) this.form.titulo = result.title
        if (result.content) this.form.conteudo = result.content
        if (result.excerpt) this.form.resumo = result.excerpt
        this.statusMessage = 'Conteúdo melhorado!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro ao melhorar conteúdo: ' + (e.response?.data?.error || e.message || 'verifique a chave da API')
        this.statusVariant = 'danger'
      }
      this.aiLoading = false
      this.aiAction = ''
    },
    async reviewContent() {
      if (!this.form.conteudo) return
      this.aiLoading = true
      this.aiAction = 'review'
      this.reviewResult = null
      try {
        const { data } = await this.$store.dispatch('blog/blogAi', {
          action: 'review', title: this.form.titulo, content: this.form.conteudo, excerpt: this.form.resumo,
        })
        this.reviewResult = typeof data.result === 'string' ? JSON.parse(data.result) : data.result
        this.statusMessage = 'Avaliação concluída!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro na avaliação: ' + (e.response?.data?.error || e.message || 'verifique a chave da API')
        this.statusVariant = 'danger'
      }
      this.aiLoading = false
      this.aiAction = ''
    },
    async generateSeo() {
      if (!this.form.titulo) return
      this.aiLoading = true
      this.aiAction = 'seo'
      try {
        const { data } = await this.$store.dispatch('blog/blogAi', {
          action: 'seo', title: this.form.titulo, content: this.form.conteudo,
        })
        const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result
        if (result.meta_title) this.form.meta_title = result.meta_title
        if (result.meta_description) this.form.meta_description = result.meta_description
        if (result.keywords) this.form.keywords = result.keywords
        this.statusMessage = 'SEO gerado!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro ao gerar SEO: ' + (e.response?.data?.error || e.message || 'verifique a chave da API')
        this.statusVariant = 'danger'
      }
      this.aiLoading = false
      this.aiAction = ''
    },
    async generateFeaturedImage() {
      if (!this.form.titulo) return
      this.aiLoading = true
      this.aiAction = 'generate-image'
      try {
        const { data } = await this.$store.dispatch('blog/blogAi', {
          action: 'generate-image', title: this.form.titulo, excerpt: this.form.resumo,
        })
        const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result
        const imageUrl = this.resolveApiUrl(result.url)
        if (imageUrl) {
          this.form.imagem_destaque = imageUrl
          if (!this.form.og_image) this.form.og_image = imageUrl
        }
        this.statusMessage = 'Imagem gerada!'
        this.statusVariant = 'success'
      } catch (e) {
        const errMsg = e.response?.data?.error || e.message || ''
        this.statusMessage = 'Erro ao gerar imagem: ' + (errMsg || 'verifique se a chave da API está configurada em Blog > Agente de IA')
        this.statusVariant = 'danger'
      }
      this.aiLoading = false
      this.aiAction = ''
    },
    async seoOptimize(failingChecks) {
      if (!this.form.titulo && !this.form.conteudo) {
        this.statusMessage = 'Adicione pelo menos um título ou conteúdo antes de otimizar'
        this.statusVariant = 'warning'
        return
      }
      this.aiLoading = true
      this.aiAction = 'seo-optimize'
      try {
        const { data } = await this.$store.dispatch('blog/blogAi', {
          action: 'seo-optimize',
          title: this.form.titulo,
          content: this.form.conteudo,
          excerpt: this.form.resumo,
          meta_title: this.form.meta_title,
          meta_description: this.form.meta_description,
          keywords: this.form.keywords,
          failing_checks: failingChecks,
        })
        const result = typeof data.result === 'string' ? JSON.parse(data.result) : data.result
        if (result.meta_title) this.form.meta_title = result.meta_title
        if (result.meta_description) this.form.meta_description = result.meta_description
        if (result.keywords) this.form.keywords = result.keywords
        if (result.excerpt) this.form.resumo = result.excerpt
        if (result.content) this.form.conteudo = result.content
        this.statusMessage = 'SEO otimizado com sucesso!'
        this.statusVariant = 'success'
      } catch (e) {
        this.statusMessage = 'Erro ao otimizar SEO: ' + (e.response?.data?.error || e.message || 'verifique a chave da API')
        this.statusVariant = 'danger'
      }
      this.aiLoading = false
      this.aiAction = ''
    },
    async createCategory() {
      if (!this.newCatName) return
      const slug = this.newCatName.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
      try {
        const { data } = await this.$store.dispatch('blog/createCategoria', { nome: this.newCatName, slug, cor: this.newCatColor })
        this.categorias.push(data)
        this.form.id_categoria = data.id
        this.newCatName = ''
        this.showNewCat = false
      } catch (e) {
        this.statusMessage = 'Erro ao criar categoria'
        this.statusVariant = 'danger'
      }
    },
    onWordPressImported(data) {
      if (data.title) this.form.titulo = data.title
      if (data.content) this.form.conteudo = data.content
      if (data.excerpt) this.form.resumo = data.excerpt
      if (data.featuredImage) this.form.imagem_destaque = this.resolveApiUrl(data.featuredImage)
      this.autoSlug()
      this.statusMessage = 'Conteúdo importado do WordPress!'
      this.statusVariant = 'success'
    },
  },
}
</script>

<style scoped>
*{box-sizing:border-box}
.be{font-family:Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#1a1a2e}

.be-topbar{display:flex;justify-content:space-between;align-items:center;padding:14px 0;margin-bottom:20px;flex-wrap:wrap;gap:12px}
.be-topbar-left{display:flex;align-items:center;gap:12px}
.be-back{width:36px;height:36px;border-radius:10px;border:1px solid #e2e8f0;background:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#64748b;transition:all .2s}
.be-back:hover{border-color:#7c3aed;color:#7c3aed;box-shadow:0 2px 8px rgba(124,58,237,.12)}
.be-topbar-title h5{font-size:1.15rem;font-weight:700;margin:0;color:#1a1a2e}
.be-slug{font-size:.75rem;color:#94a3b8}
.be-topbar-actions{display:flex;gap:8px;flex-wrap:wrap}

.be-btn{display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:10px;font-size:.82rem;font-weight:600;border:none;cursor:pointer;transition:all .2s cubic-bezier(.4,0,.2,1);white-space:nowrap}
.be-btn:disabled{opacity:.5;cursor:not-allowed;transform:none!important}
.be-btn-ghost{background:#f8fafc;color:#64748b;border:1px solid #e2e8f0}
.be-btn-ghost:hover:not(:disabled){background:#f1f5f9;color:#475569}
.be-btn-outline{background:#fff;color:#7c3aed;border:1.5px solid #7c3aed}
.be-btn-outline:hover:not(:disabled){background:#f5f3ff;box-shadow:0 2px 8px rgba(124,58,237,.12)}
.be-btn-primary{background:#7c3aed;color:#fff}
.be-btn-primary:hover:not(:disabled){background:#6d28d9;box-shadow:0 4px 14px rgba(124,58,237,.3);transform:translateY(-1px)}
.be-btn-sm{padding:6px 12px;font-size:.78rem;border-radius:8px}
.be-btn-block{width:100%}

.be-toast{display:flex;align-items:center;gap:10px;padding:12px 18px;border-radius:12px;margin-bottom:16px;font-size:.85rem;font-weight:500;cursor:pointer;animation:slideDown .3s ease}
.be-toast-close{margin-left:auto;opacity:.6}
.be-toast-success{background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0}
.be-toast-warning{background:#fffbeb;color:#92400e;border:1px solid #fde68a}
.be-toast-danger{background:#fef2f2;color:#991b1b;border:1px solid #fecaca}
@keyframes slideDown{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}

.be-layout{display:grid;grid-template-columns:1fr 340px;gap:24px;align-items:start}
@media(max-width:992px){.be-layout{grid-template-columns:1fr}}

.be-tabs{display:flex;gap:4px;padding:4px;background:#f8fafc;border-radius:12px;margin-bottom:20px;border:1px solid #e2e8f0}
.be-tab{display:flex;align-items:center;gap:6px;padding:10px 18px;border-radius:10px;border:none;background:transparent;font-size:.82rem;font-weight:600;color:#64748b;cursor:pointer;transition:all .2s}
.be-tab.active{background:#fff;color:#7c3aed;box-shadow:0 1px 3px rgba(0,0,0,.08)}
.be-tab:hover:not(.active){color:#475569;background:#f1f5f9}

.be-panel{background:#fff;border-radius:14px;padding:24px;border:1px solid #e2e8f0;box-shadow:0 1px 3px rgba(0,0,0,.04)}

.be-field{margin-bottom:20px}
.be-label{display:block;font-size:.78rem;font-weight:600;color:#475569;margin-bottom:6px;text-transform:uppercase;letter-spacing:.4px}
.be-input{width:100%;padding:10px 14px;border:1.5px solid #e2e8f0;border-radius:10px;font-size:.88rem;color:#1a1a2e;background:#fff;transition:all .2s;outline:none;font-family:inherit}
.be-input:focus{border-color:#7c3aed;box-shadow:0 0 0 3px rgba(124,58,237,.1)}
.be-input::placeholder{color:#94a3b8}
.be-input-lg{font-size:1.15rem;font-weight:600;padding:14px 16px}
.be-textarea{resize:vertical;min-height:60px}
.be-slug-field{display:flex;align-items:center;border:1.5px solid #e2e8f0;border-radius:10px;overflow:hidden;transition:all .2s}
.be-slug-field:focus-within{border-color:#7c3aed;box-shadow:0 0 0 3px rgba(124,58,237,.1)}
.be-slug-prefix{padding:10px 0 10px 14px;font-size:.85rem;color:#94a3b8;white-space:nowrap;background:#f8fafc}
.be-input-slug{border:none;border-radius:0;padding-left:4px}
.be-input-slug:focus{box-shadow:none}
.be-counter{font-size:.72rem;color:#94a3b8;margin-top:4px;display:block}
.be-counter.over{color:#ef4444}
.be-select{appearance:none;background-image:url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");background-position:right 10px center;background-repeat:no-repeat;background-size:20px;padding-right:36px}

.be-seo-grid{display:flex;flex-direction:column;gap:16px}
.be-seo-card{display:flex;gap:14px;padding:16px;border:1px solid #e2e8f0;border-radius:12px;background:#fafbfc;transition:border-color .2s}
.be-seo-card:focus-within{border-color:#7c3aed;background:#fff}
.be-seo-card-icon{width:36px;height:36px;border-radius:10px;background:#f0ebff;color:#7c3aed;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
.be-seo-card-body{flex:1;min-width:0}
.be-seo-card-body .be-input{border-color:transparent;padding:8px 10px;background:transparent}
.be-seo-card-body .be-input:focus{border-color:#7c3aed;background:#fff}
.be-char-bar{height:3px;background:#e2e8f0;border-radius:3px;margin-top:6px;overflow:hidden}
.be-char-fill{height:100%;border-radius:3px;background:#e2e8f0;transition:all .3s}
.be-char-fill.ok{background:#10b981}
.be-char-fill.over{background:#ef4444}

.be-ai-grid{display:flex;flex-direction:column;gap:16px}
.be-ai-card{padding:20px;border:1px solid #e2e8f0;border-radius:14px;background:#fff;transition:all .25s}
.be-ai-card:hover{box-shadow:0 4px 16px rgba(0,0,0,.06);border-color:#ddd6fe}
.be-ai-card-top{display:flex;gap:14px;margin-bottom:14px}
.be-ai-card-top h6{font-size:.92rem;font-weight:700;color:#1a1a2e;margin:0 0 2px}
.be-ai-card-top p{font-size:.8rem;color:#64748b;margin:0;line-height:1.4}
.be-ai-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;color:#fff;flex-shrink:0}
.be-ai-icon.purple{background:linear-gradient(135deg,#7c3aed,#a78bfa)}
.be-ai-icon.blue{background:linear-gradient(135deg,#0ea5e9,#67e8f9)}
.be-ai-icon.amber{background:linear-gradient(135deg,#f59e0b,#fbbf24)}

.be-review{margin-top:16px;padding:16px;border-radius:12px;background:#f8fafc;border:1px solid #e2e8f0}
.be-review-score{display:flex;align-items:baseline;gap:2px;margin-bottom:14px}
.be-review-num{font-size:2rem;font-weight:800;line-height:1}
.be-review-num.good{color:#10b981}
.be-review-num.mid{color:#f59e0b}
.be-review-num.low{color:#ef4444}
.be-review-of{font-size:.9rem;color:#94a3b8;font-weight:600}
.be-review-list{margin-bottom:12px}
.be-review-list strong{display:flex;align-items:center;gap:6px;font-size:.8rem;margin-bottom:6px}
.be-review-list.good strong{color:#10b981}
.be-review-list.warn strong{color:#f59e0b}
.be-review-list ul{padding-left:16px;margin:0}
.be-review-list li{font-size:.8rem;color:#475569;margin-bottom:3px;line-height:1.5}
.be-review-summary{font-size:.8rem;color:#94a3b8;margin:8px 0 0;font-style:italic}

.be-sidebar{display:flex;flex-direction:column;gap:16px;position:sticky;top:20px}
.be-side-card{background:#fff;border:1px solid #e2e8f0;border-radius:14px;padding:18px;box-shadow:0 1px 3px rgba(0,0,0,.04)}
.be-side-label{font-size:.78rem;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.4px;margin-bottom:10px;display:flex;align-items:center;gap:6px}
.be-link{display:inline-flex;align-items:center;gap:4px;background:none;border:none;color:#7c3aed;font-size:.78rem;font-weight:600;cursor:pointer;padding:4px 0;margin-top:8px;transition:color .2s}
.be-link:hover{color:#6d28d9}
.be-link:disabled{color:#94a3b8;cursor:not-allowed}
.be-inline-form{display:flex;gap:8px;align-items:center;margin-top:10px;flex-wrap:wrap}
.be-inline-form .be-input{flex:1;min-width:100px}
.be-color{width:36px;height:36px;border:1.5px solid #e2e8f0;border-radius:8px;padding:2px;cursor:pointer;background:#fff}
.be-img-preview{position:relative;border-radius:12px;overflow:hidden;border:1px solid #e2e8f0}
.be-img-preview img{width:100%;height:140px;object-fit:cover;display:block}
.be-img-remove{position:absolute;top:8px;right:8px;width:28px;height:28px;border-radius:8px;border:none;background:rgba(0,0,0,.5);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .2s}
.be-img-remove:hover{background:rgba(239,68,68,.9)}
.be-img-dropzone{position:relative;border:2px dashed #e2e8f0;border-radius:12px;padding:24px;text-align:center;color:#94a3b8;display:flex;flex-direction:column;align-items:center;gap:6px;transition:all .2s;cursor:pointer}
.be-img-dropzone:hover{border-color:#7c3aed;color:#7c3aed;background:#faf5ff}
.be-img-dropzone span{font-size:.82rem;font-weight:500}
.be-img-input{position:absolute;inset:0;opacity:0;cursor:pointer}
</style>
